import TableOnly from "components/DataTable/TableOnly";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import authHeader from "services/auth-header";
import API from "services/axios";
import { convertSqlTimestampToDate } from "utils/UtilityFuncs";
import PaginationWithState from "components/Pagination/PaginationWithState";
import * as XLSX from "xlsx";

export default function AllTransactions() {
  /*   All States
   ********************************************* */
  const dispatch = useDispatch();

  const [startDate, setStartDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 2)
      .toISOString()
      .slice(0, 10)
      .replace("T", " ");
  });

  const [endDate, setEndDate] = useState(
    new Date().toISOString().slice(0, 10).replace("T", " ")
  );

  const [sortBy, setSortBy] = useState("none");
  const [sortDirection, setSortDirection] = useState("asc");

  const [searchResults, setSearchResults] = useState(null);

  const [showDateFilter, setShowDateFilter] = useState(false);
  const [allTransactionsReport, setAllTransactionsReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  /*   All Functions
   ********************************************* */
  //get AllTransactions Report
  const getAllTransactionsReport = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      try {
        setLoading(true);

        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;

        // get only date frm endDate not time
        const endDateObj = new Date(endDate);
        const endDateString = endDateObj.toISOString().slice(0, 10);

        const { data } = await API.post(
          "/manufacturer-material-list-transactions?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            startDate: startDate,
            endDate: endDateString,
            sortBy: sortBy,
            sortDirection: sortDirection,
            query: usedQuery,
          },
          { headers: authHeader() }
        );
        if (data?.success) {
          setAllTransactionsReport(data?.data || []);
          setTotalPages(data?.totalPages || 0);
        }
      } catch (err) {
        dispatch(
          setNotification({
            message: err.message,
            type: "error",
          })
        );
      } finally {
        setLoading(false);
      }
    },
    [pageNumber, pageLimit, sortBy, sortDirection, startDate, endDate, dispatch]
  );

  const handleReportDownload = async () => {
    try {
      setLoading(true);
      const { data } = await API.get("/manufacturer-materials-available-qty", {
        headers: authHeader(),
      });

      const rows = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
        ? data
        : [];

      if (!rows.length) {
        dispatch(
          setNotification({
            message: "No data found for export",
            type: "warning",
          })
        );
        return;
      }

      const normalizedRows = rows.map((item) => ({
        MaterialID: item.materialId,
        Type: item.type,
        Description: item.description,
        StyleId: item.styleId,
        Color: item.color,
        GSM: item.gsm,
        // MinQty: item.minQty,
        AvailableQty: item.availableQty,
        TotalPrice: item.lotTotalPrice,
        // TotalPurchasedQtyUsed: item.totalPurchasedQtyUsed,
        // TotalReturnQty: item.totalReturnQty,
      }));

      const worksheet = XLSX.utils.json_to_sheet(normalizedRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "materials");
      XLSX.writeFile(workbook, "materials-available-qty.xlsx");

      dispatch(
        setNotification({
          message: "Report exported",
          type: "success",
        })
      );
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  /*   All UseEffects
   ********************************************* */
  useEffect(() => {
    let timeout = setTimeout(() => {
      getAllTransactionsReport({ page: pageNumber });
    }, 50);
    return () => clearTimeout(timeout);
  }, [getAllTransactionsReport, pageNumber, pageLimit]);

  useEffect(() => {
    if (allTransactionsReport.length === 0) return;

    let sortedData = [...allTransactionsReport];

    if (sortBy !== "none") {
      sortedData.sort((a, b) => {
        let fieldA = a[sortBy];
        let fieldB = b[sortBy];

        if (sortDirection === "asc") {
          return fieldA < fieldB ? -1 : fieldA > fieldB ? 1 : 0;
        } else {
          return fieldA > fieldB ? -1 : fieldA < fieldB ? 1 : 0;
        }
      });
    }

    setAllTransactionsReport(sortedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortDirection]);

  const table_head = [
    { name: "Type", value: "" },
    { name: "T-ID", value: "" },
    { name: "Product Type", value: "" },
    { name: "Style No#", value: "" },
    { name: "Color", value: "" },
    { name: "Qty", value: "" },
    { name: "Date", value: "" },
    { name: "Vendor", value: "" },
    { name: "Lot", value: "" },
    { name: "Action", value: "" },
  ];

  const issueRow = (item) => {
    return (
      <tr className="" key={item.id}>
        <td className="align-middle py-1">
          <span>{item?.type?.toUpperCase()}</span>
        </td>
        <td className="align-middle py-1">{item?.tid}</td>
        <td className="align-middle py-1">
          {item?.m_issues?.[0]?.m_materialList?.type}
        </td>
        <td className="align-middle py-1">
          {item?.m_issues?.[0]?.m_materialList?.styleId}
        </td>
        <td className="align-middle py-1">
          {item?.m_issues?.[0]?.m_materialList?.color}
        </td>
        <td className="align-middle py-1">{item?.m_issues?.[0]?.qty}</td>
        <td className="align-middle py-1">
          {convertSqlTimestampToDate(item?.createdAt)}
        </td>
        <td className="align-middle py-1">
          {item?.m_issues?.[0]?.m_purchase?.vendorName}
        </td>
        <td className="align-middle py-1">{item?.m_issues?.[0]?.lotId}</td>
        <td className="align-middle py-1">
          {item?.m_issues?.length === 0 && item?.m_purchases?.length === 0 ? (
            // if no issues or purchases, Indicate as Deleted
            <button className="btn btn-sm btn-danger ms-1" disabled>
              Deleted
            </button>
          ) : (
            <Link
              to={
                "/manufacturer/material-stock?tab=material-issue&materialId=" +
                item?.m_issues[0]?.materialId +
                "&page=1&limit=10&lotId=" +
                item?.m_issues[0]?.lotId +
                "&tid=" +
                item?.tid
              }
              className="btn btn-sm btn-success ms-1"
            >
              Issue
            </Link>
          )}
        </td>
      </tr>
    );
  };

  const purchaseRow = (item) => {
    return (
      <tr className="" key={item.id}>
        <td className="align-middle py-1">
          <span>{item?.type?.toUpperCase()}</span>
        </td>
        <td className="align-middle py-1">{item?.tid}</td>
        <td className="align-middle py-1">
          {item?.m_purchases?.[0]?.m_materialList?.type}
        </td>
        <td className="align-middle py-1">
          {item?.m_purchases?.[0]?.m_materialList?.styleId}
        </td>
        <td className="align-middle py-1">
          {item?.m_purchases?.[0]?.m_materialList?.color}
        </td>
        <td className="align-middle py-1">
          {item?.m_purchases?.[0]?.purchasedQty}
        </td>
        <td className="align-middle py-1">
          {convertSqlTimestampToDate(item?.createdAt)}
        </td>
        <td className="align-middle py-1">
          {item?.m_purchases?.[0]?.vendorName}
        </td>
        <td className="align-middle py-1">{item?.m_purchases?.[0]?.lotId}</td>
        <td className="align-middle py-1">
          <Link
            to={
              "/manufacturer/material-stock?tab=material-issue&materialId=" +
              item?.m_purchases[0]?.materialId +
              "&page=1&limit=10&lotId=" +
              item?.m_purchases[0]?.lotId +
              "&tid=" +
              item?.tid
            }
            className="btn btn-sm btn-success ms-1"
          >
            Purchase
          </Link>
        </td>
      </tr>
    );
  };

  const table_body = (item, index) => {
    if (item?.m_issues?.length === 0 && item?.m_purchases?.length === 0) {
      return (
        <tr className="" key={item.id}>
          <td className="align-middle py-1">{item?.type?.toUpperCase()}</td>
          <td className="align-middle py-1">{item?.tid}</td>
          <td className="align-middle py-1"></td>
          <td className="align-middle py-1"></td>
          <td className="align-middle py-1"></td>
          <td className="align-middle py-1"></td>
          <td className="align-middle py-1"></td>
          <td className="align-middle py-1"></td>
          <td className="align-middle py-1"></td>
          <td className="align-middle py-1">
            <button className="btn btn-sm btn-danger ms-1" disabled>
              Deleted
            </button>
          </td>
        </tr>
      );
    } else if (item?.type === "issue") {
      return issueRow(item);
    } else if (item?.type === "purchase") {
      return purchaseRow(item);
    }

    return null;

    //   </td>
    //   <td className="align-middle py-1">{item?.tid}</td>
    //   <td className="align-middle py-1">
    //     {item?.m_issues?.length === 0 && item?.m_purchases?.length === 0 ? (
    //       // if no issues or purchases, Indicate as Deleted
    //       <button className="btn btn-sm btn-danger ms-1" disabled>
    //         Deleted
    //       </button>
    //     ) : item?.type === "issue" ? (
    //       <Link
    //         to={
    //           "/manufacturer/material-stock?tab=material-issue&materialId=" +
    //           item?.m_issues[0]?.materialId +
    //           "&page=1&limit=10&lotId=" +
    //           item?.m_issues[0]?.lotId +
    //           "&tid=" +
    //           item?.tid
    //         }
    //         className="btn btn-sm btn-success ms-1"
    //       >
    //         Issue
    //       </Link>
    //     ) : (
    //       <Link
    //         to={
    //           "/manufacturer/material-stock?tab=material-purchase&materialId=" +
    //           item?.m_purchases[0]?.materialId +
    //           "&page=1&limit=10&tid=" +
    //           item?.tid
    //         }
    //         className="btn btn-sm btn-success ms-1"
    //       >
    //         Purchase
    //       </Link>
    //     )}
    //   </td>
    // </tr>
  };

  return (
    <div className="mt-3 ps-2 pe-3">
      {loading && <Spinner />}

      <div className="d-flex justify-content-between mb-3 align-items-center">
        <div>
          Show &nbsp;
          <div className="btn-group">
            <button
              className="px-1 bg-primary bg-opacity-10 border border-primary rounded-end rounded-start "
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              {pageLimit}
            </button>
            <ul className="dropdown-menu w-25">
              <li onClick={() => setPageLimit(5)}>
                <button className="dropdown-item">5</button>
              </li>
              <li onClick={() => setPageLimit(10)}>
                <button className="dropdown-item">10</button>
              </li>
              <li onClick={() => setPageLimit(15)}>
                <button className="dropdown-item">15</button>
              </li>
              <li onClick={() => setPageLimit(20)}>
                <button className="dropdown-item">20</button>
              </li>
            </ul>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleReportDownload}
          >
            Report
          </button>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getAllTransactionsReport({ query: searchTerm, page: 1 });
              } else {
                getAllTransactionsReport({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getAllTransactionsReport({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>
      </div>

      <div className="horizontal-scrollable d-flex flex-grow-1">
        <TableOnly
          searchMaxWidth={"300px"}
          table_head={table_head}
          table_body={table_body}
          table_data={allTransactionsReport}
          actionsOnEnd={true}
          disableSorting={true}
        />
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>
    </div>
  );
}
